import type { UnifiedReviewItem } from '@/types/review';

/**
 * States how a review's link to a visit was decided.
 *
 * The drawer renders a linked visit with a green check whether a human verified it or
 * the sync matched two names inside a 90-day window. The manual-link form a few lines
 * below warns that "matching names and dates alone are not proof" — this note is what
 * stops the automatic path from contradicting the warning the same screen gives.
 *
 * Renders nothing for `CLICK_ATTRIBUTED` (A2 owns that copy; nothing writes the value
 * yet), for a plain refusal, and for rows predating the column. Silence is correct in
 * all three: inventing reassuring text for a state we know nothing about is the failure
 * this component exists to prevent.
 */
export function AttributionConfidenceNote({
  confidence,
  ambiguousCandidateCount,
}: {
  confidence?: UnifiedReviewItem['attributionConfidence'];
  ambiguousCandidateCount?: number;
}) {
  if (confidence === 'INFERRED') {
    return <p className="text-xs text-muted-foreground">Matched by name and date — unverified.</p>;
  }

  if (confidence === 'MANUAL') {
    return <p className="text-xs text-muted-foreground">Linked manually.</p>;
  }

  // Only worth saying when there was a genuine collision. A count of one would mean the
  // match succeeded, and a refusal with nothing competing is just "no match".
  if (confidence === 'UNATTRIBUTED' && (ambiguousCandidateCount ?? 0) > 1) {
    return (
      <p className="text-xs text-muted-foreground">
        Couldn&apos;t link — {ambiguousCandidateCount} visits matched this name. Link the right one
        below.
      </p>
    );
  }

  return null;
}
